import React from "react";

/**
 * IndianHeritageBackdrop
 * Subtle, low-opacity SVG of the Indian Parliament dome + Ashoka pillar
 * silhouette, designed to sit behind the V9 hero on dark navy.
 * Adds cultural anchoring without competing with the diagnostic dashboard.
 */
export const IndianHeritageBackdrop = () => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Ashoka Chakra — large hero anchor, right side, slow rotation */}
      <div
        className="absolute top-1/2 right-[-160px] md:right-[-100px] -translate-y-1/2 w-[420px] h-[420px] md:w-[560px] md:h-[560px] lg:w-[640px] lg:h-[640px]"
      >
        {/* Soft amber halo behind chakra */}
        <div
          className="absolute inset-0 rounded-full blur-3xl opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.35) 0%, rgba(245,158,11,0) 65%)' }}
        />

        {/* Tricolor outer ring (very subtle, decorative) */}
        <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full opacity-25" aria-hidden="true">
          <defs>
            <linearGradient id="tricolor-top" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FB923C" stopOpacity="0" />
              <stop offset="50%" stopColor="#FB923C" stopOpacity="1" />
              <stop offset="100%" stopColor="#FB923C" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="tricolor-bot" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#16A34A" stopOpacity="0" />
              <stop offset="50%" stopColor="#16A34A" stopOpacity="1" />
              <stop offset="100%" stopColor="#16A34A" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Saffron arc on top */}
          <path d="M 80 300 A 220 220 0 0 1 520 300" fill="none" stroke="url(#tricolor-top)" strokeWidth="3" />
          {/* Green arc on bottom */}
          <path d="M 520 300 A 220 220 0 0 1 80 300" fill="none" stroke="url(#tricolor-bot)" strokeWidth="3" />
        </svg>

        {/* The Chakra itself */}
        <svg
          viewBox="0 0 200 200"
          className="absolute inset-0 w-full h-full opacity-[0.13]"
          style={{ animation: "spin 120s linear infinite" }}
        >
          <defs>
            <radialGradient id="chakra-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FCD34D" stopOpacity="1" />
              <stop offset="70%" stopColor="#F59E0B" stopOpacity="1" />
              <stop offset="100%" stopColor="#EA580C" stopOpacity="0.8" />
            </radialGradient>
          </defs>
          {/* Outer ring */}
          <circle cx="100" cy="100" r="94" fill="none" stroke="url(#chakra-grad)" strokeWidth="2.5" />
          <circle cx="100" cy="100" r="88" fill="none" stroke="url(#chakra-grad)" strokeWidth="1" opacity="0.6" />
          {/* Hub */}
          <circle cx="100" cy="100" r="8" fill="url(#chakra-grad)" />
          <circle cx="100" cy="100" r="3" fill="#FCD34D" />
          {/* 24 spokes */}
          {Array.from({ length: 24 }).map((_, i) => (
            <line
              key={i}
              x1="100"
              y1="100"
              x2="100"
              y2="10"
              stroke="url(#chakra-grad)"
              strokeWidth="1.6"
              transform={`rotate(${i * 15} 100 100)`}
            />
          ))}
          {/* Inner decorative dots between spokes — gives the 24-petal look */}
          {Array.from({ length: 24 }).map((_, i) => (
            <circle
              key={`dot-${i}`}
              cx="100"
              cy="22"
              r="2"
              fill="#FCD34D"
              transform={`rotate(${i * 15 + 7.5} 100 100)`}
            />
          ))}
        </svg>
      </div>

      {/* Parliament dome silhouette — bottom of hero, very subtle */}
      <svg
        viewBox="0 0 1200 240"
        preserveAspectRatio="xMidYMax slice"
        className="absolute bottom-0 left-0 right-0 w-full h-56 md:h-64 opacity-[0.07]"
      >
        <defs>
          <linearGradient id="dome-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.0" />
            <stop offset="60%" stopColor="#F59E0B" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Center: Parliament round dome with pillars */}
        <g fill="url(#dome-grad)">
          {/* Main dome cap */}
          <ellipse cx="600" cy="120" rx="180" ry="28" />
          <rect x="420" y="120" width="360" height="110" />
          {/* Mini pillars across the round body */}
          {Array.from({ length: 20 }).map((_, i) => (
            <rect
              key={i}
              x={420 + i * 18 + 4}
              y={130}
              width="6"
              height="100"
            />
          ))}
          {/* Central flag mast */}
          <rect x="598" y="80" width="4" height="40" />
          <polygon points="602,82 622,90 602,98" />
        </g>

        {/* Left: India Gate arch */}
        <g fill="url(#dome-grad)">
          <path d="M 120 230 L 120 150 Q 120 100, 170 100 L 220 100 Q 270 100, 270 150 L 270 230 Z" />
          <rect x="100" y="220" width="190" height="14" />
        </g>

        {/* Right: Lion Capital pedestal abstraction */}
        <g fill="url(#dome-grad)">
          <rect x="940" y="200" width="160" height="32" />
          <rect x="960" y="170" width="120" height="34" />
          {/* Four lions abstraction — domed silhouettes */}
          <ellipse cx="985" cy="160" rx="18" ry="14" />
          <ellipse cx="1020" cy="160" rx="18" ry="14" />
          <ellipse cx="1055" cy="160" rx="18" ry="14" />
        </g>
      </svg>

      {/* Tricolor vertical accents (very subtle) — left edge */}
      <div className="absolute left-0 top-1/3 bottom-1/3 w-1 flex flex-col opacity-25">
        <div className="flex-1 bg-orange-500" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-green-600" />
      </div>
    </div>
  );
};

export default IndianHeritageBackdrop;
